import { LIMITS } from '../data/limits';
import type { RewriteVariant, RuleContext } from '../types';
import { PLACEHOLDER, extractFacts } from './facts';

/**
 * Headline scaffolds.
 *
 * Three variants rather than one, because there is no single correct headline —
 * the right shape depends on whether the reader is a recruiter searching by
 * keyword, a hiring manager skimming a shortlist, or a peer deciding whether to
 * accept a connection. Offering three also makes it obvious these are starting
 * points to edit, not output to paste.
 *
 * Every bracketed token is a placeholder the user must replace. See `facts.ts`.
 */

/** Trims a scaffold to LinkedIn's hard limit on a word boundary. */
function fit(text: string): string {
  const limit = LIMITS.headline.hard;
  if (text.length <= limit) return text;
  const slice = text.slice(0, limit);
  const cut = slice.lastIndexOf(' ');
  return (cut > limit * 0.6 ? slice.slice(0, cut) : slice).trimEnd();
}

export function buildHeadlineVariants(ctx: RuleContext): RewriteVariant[] {
  const facts = extractFacts(ctx);
  const { role, specialisation } = facts;

  const keywordTail =
    facts.topSkills.length > 0
      ? facts.topSkills.slice(0, 4).join(' • ')
      : `${PLACEHOLDER.specialisation} • ${PLACEHOLDER.specialisation}`;

  return [
    {
      id: 'concise',
      label: 'Concise',
      description:
        'Three clear parts, front-loaded so the important half survives truncation in search results and on mobile.',
      text: fit(`${role} | ${specialisation} | ${PLACEHOLDER.outcome}`),
    },
    {
      id: 'keyword-rich',
      label: 'Keyword-rich',
      description:
        'Optimised for recruiter search: packs in the terms people filter by. Least natural to read, most likely to be found.',
      text: fit(
        `${role} | ${keywordTail}${
          facts.missingKeywords.length > 0 ? ` • ${facts.missingKeywords.join(' • ')}` : ''
        }`
      ),
    },
    {
      id: 'impact-first',
      label: 'Impact-first',
      description:
        'Leads with the result rather than the title. Strongest when your work is easier to explain than your job title.',
      text: fit(
        `Helping ${PLACEHOLDER.audience} ${PLACEHOLDER.outcome} | ${role} | ${specialisation}`
      ),
    },
  ];
}
